import Configuration from '@/api/Configuration';
import {
  toApiOrganizationCreateRequest,
  toApiOrganizationUpdateRequest,
  toOrganization,
} from '@/api/mappers/mapOrganization';
import {
  OrganizationsApi as ApiOrganizationsApi,
  CreateOrganizationRequest,
  ListOrganizationsRequest,
  UpdateOrganizationRequest,
} from '@/gen/api/apis/OrganizationsApi';
import { Organization, OrganizationCreate, Organizations } from '@/models/Organization';

interface OrganizationsApi {
  createOrganization(organization: OrganizationCreate): Promise<void>;
  deleteOrganization(id: string): Promise<void>;
  getOrganization(id: string): Promise<Organization | undefined>;
  listOrganizations(offset?: number, limit?: number): Promise<Organizations>;
  findOrganizations(query?: string, offset?: number, limit?: number): Promise<Organizations>;
  updateOrganization(id: string, organization: Organization): Promise<void>;
}

export class OrganizationsApiImpl implements OrganizationsApi {
  private organizationsApi: ApiOrganizationsApi;

  constructor(configuration: Configuration) {
    this.organizationsApi = new ApiOrganizationsApi(configuration);
  }

  async listOrganizations(offset = 0, limit = 10): Promise<Organizations> {
    try {
      const response = await this.organizationsApi.listOrganizations({ offset, limit });

      return {
        totalCount: response.totalCount,
        estimatedTotalCount: response.estimatedTotalCount,
        items: response.items.map(toOrganization),
      };
    } catch (error) {
      console.error('Error listing organizations:', error);
      throw error;
    }
  }

  async findOrganizations(query?: string, offset = 0, limit = 10): Promise<Organizations> {
    try {
      const params: ListOrganizationsRequest = { query, offset, limit };
      const response = await this.organizationsApi.listOrganizations(params);

      return {
        totalCount: response.totalCount,
        estimatedTotalCount: response.estimatedTotalCount,
        items: response.items.map(toOrganization),
      };
    } catch (error) {
      console.error('Error finding organizations:', error);
      throw error;
    }
  }

  async createOrganization(organization: OrganizationCreate): Promise<void> {
    try {
      const params: CreateOrganizationRequest = {
        organizationCreateRequest: toApiOrganizationCreateRequest(organization),
      };
      await this.organizationsApi.createOrganization(params);
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  }

  async getOrganization(id: string): Promise<Organization | undefined> {
    try {
      const response = await this.organizationsApi.getOrganization({ id });
      return toOrganization(response);
    } catch (error) {
      console.error('Error getting organization:', error);
      throw error;
    }
  }

  async updateOrganization(id: string, organization: Organization): Promise<void> {
    try {
      const params: UpdateOrganizationRequest = {
        id,
        organizationUpdateRequest: toApiOrganizationUpdateRequest(organization),
      };
      await this.organizationsApi.updateOrganization(params);
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  }

  async deleteOrganization(id: string): Promise<void> {
    try {
      await this.organizationsApi.deleteOrganization({ id });
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  }
}

export default OrganizationsApi;
